
import React from 'react';
import { 
  LayoutDashboard, 
  ClipboardList, 
  CalendarDays, 
  ShieldAlert, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  Users,
  ShieldCheck
} from 'lucide-react';
import { UserRole, AppUser, AppFeature, RolePermissions } from '../types';

interface LayoutProps {
  children: React.ReactNode;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  user: AppUser;
  onLogout: () => void;
  permissions: RolePermissions;
}

const Layout: React.FC<LayoutProps> = ({ children, activeTab, setActiveTab, user, onLogout, permissions }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const menuItems = [
    { id: AppFeature.DASHBOARD, label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { id: AppFeature.MASTER_DATA, label: 'Master Repository', icon: <Settings className="w-5 h-5" /> },
    { id: AppFeature.SCHEDULE, label: 'Schedule Plan', icon: <CalendarDays className="w-5 h-5" /> },
    { id: AppFeature.INPUT_ACTUAL, label: 'Input Aktual', icon: <ClipboardList className="w-5 h-5" /> },
    { id: AppFeature.MONITORING, label: 'Plan vs Aktual', icon: <ShieldAlert className="w-5 h-5" /> },
    { id: AppFeature.FINDINGS, label: 'CAPA Tracking', icon: <ShieldAlert className="w-5 h-5 text-red-400" /> },
    { id: AppFeature.REPORTS, label: 'Audit Reports', icon: <FileText className="w-5 h-5" /> },
    { id: AppFeature.USERS, label: 'User Control', icon: <Users className="w-5 h-5 text-emerald-400" /> },
  ];

  // Ambil fitur yang diperbolehkan untuk role user saat ini
  const userAllowedFeatures = permissions[user.role] || [];
  const filteredMenu = menuItems.filter(item => userAllowedFeatures.includes(item.id));

  return (
    <div className="min-h-screen flex bg-gray-50 overflow-hidden font-sans">
      <button className="lg:hidden fixed top-4 left-4 z-50 p-3 bg-white rounded-2xl shadow-lg" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-80 bg-slate-950 text-white transform transition-transform duration-500 ease-in-out shadow-2xl ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          <div className="p-10 border-b border-white/5 text-center">
            <h1 className="text-2xl font-black tracking-tighter flex items-center justify-center gap-3 uppercase">
              <ShieldCheck className="w-8 h-8 text-emerald-400" /> Safety<span className="text-emerald-400">Master</span>
            </h1>
          </div>

          <nav className="flex-1 px-6 py-10 space-y-2 overflow-y-auto">
            {filteredMenu.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setIsOpen(false); }}
                className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl transition-all ${activeTab === item.id ? 'bg-emerald-600 text-white shadow-xl' : 'text-slate-500 hover:bg-white/5 hover:text-white'}`}
              >
                {item.icon}
                <span className="font-black text-xs uppercase tracking-widest">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="p-8 border-t border-white/5">
            <div className="flex items-center gap-4 bg-white/5 p-4 rounded-3xl">
              <img src={user.avatar} className="w-10 h-10 rounded-xl object-cover border-2 border-emerald-500/30" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black truncate uppercase text-white">{user.name}</p>
                <p className="text-[9px] font-bold text-emerald-400 uppercase tracking-widest">{user.role}</p>
              </div>
              <button onClick={onLogout} className="p-2 text-slate-500 hover:text-rose-400"><LogOut className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 h-screen overflow-y-auto relative bg-slate-50">
        <div className="p-10 max-w-[1600px] mx-auto min-h-full">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
